import React, { useState, useEffect } from 'react';
import _, { filter } from 'lodash';
import {
  Divider,
  Grid,
  Header,
  Segment,
  Table,
  Accordion,
  Icon,
  Button
} from 'semantic-ui-react';
import { servicesAPI, useCustomers } from '../../_services';
import { dbUTCDatetimeToLocal, getLocalAuthorityInfo } from '../../utils';
import { setToast } from '../../store/ToastManagement';
import { useDispatch } from 'react-redux';
import { history } from '../../_helpers';

const formatCurrency = (currency, amountPounds) => {
  if (isNaN(amountPounds)) return '?';
  const absAmountPounds = Math.abs(amountPounds);
  return (amountPounds < 0 ? '-' : '') + currency + absAmountPounds.toFixed(2);
};

export const ListPendingBills = () => {
  const dispatch = useDispatch();
  const displayMessage = (title, message, success) =>
    dispatch(
      setToast({
        title: title,
        success: success,
        body: message
      })
    );

  const allCustomers = useCustomers();

  const [pending_bills, setPendingBills] = useState([]);
  const [filtered_pending_bills, setFilteredPendingBills] = useState([]);
  const [pending_bills_summary, setPendingBillSummary] = useState(0);
  const [activePendingBillId, setActivePendingBill] = useState(null);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [checkedArchiveRadio, setArchiveRadioSelected] = useState('ShowActive');
  const [greenRows, addRowToGreenColour] = useState([]);
  const [greyedOutProcessButtons, addRowToGreyedOutProcessButtons] = useState(
    []
  );

  const [greyedOutModifyButtons, addRowToGreyedOutModifyButtons] = useState([]);
  const [cursorState, setCursorState] = useState('');

  const setSummaryCost = filtered => {
    let total_pending_amount = 0;
    filtered.forEach(pending_bill => {
      total_pending_amount += pending_bill.bill_cost / 1000 / 100;
    });
    setPendingBillSummary(total_pending_amount);
  };

  const onArchiveFilterChanged = x => {
    let filtered = [];
    switch (x) {
      case 'ShowAll':
        filtered = pending_bills;
        break;
      case 'ShowActive':
        filtered = pending_bills.filter(x => x.archived === false);
        break;
      case 'ShowArchived':
        filtered = pending_bills.filter(x => x.archived === true);
        break;
    }
    setFilteredPendingBills(filtered);
    setSummaryCost(filtered);
  };

  const onCreditApplied = (pending_bill_id, message) => {
    let pending_bill = filtered_pending_bills.find(
      x => x.id === pending_bill_id
    );
    pending_bill.credits_applied = message;
    onArchiveFilterChanged(checkedArchiveRadio);
  };

  const onArchiveButtonPressed = pending_bill_id => {
    let pending_bill = filtered_pending_bills.find(
      x => x.id === pending_bill_id
    );
    pending_bill.archived = !pending_bill.archived;
    onArchiveFilterChanged(checkedArchiveRadio);
  };

  useEffect(() => {
    let tariffs = [];

    servicesAPI.BillingServices('getTariffs').then(tariffsFromDb => {
      tariffs = tariffsFromDb;
    });

    if (!allCustomers.length) return;

    servicesAPI
      .BillingServices('getPendingSessionlets')
      .then(allSessionlets => {
        servicesAPI.BillingServices('getPendingBills').then(pending_bills => {
          servicesAPI
            .BillingServices('getPendingLineItems')
            .then(allLineItems => {
              pending_bills.forEach(pending_bill => {
                let pendingBillSessionLets = [];
                let currCustomerId = pending_bill.customer_id;

                const customer = allCustomers.find(
                  c => c.id === currCustomerId
                );

                const d = new Date(pending_bill.bill_timestamp);
                pending_bill.date_str = d.toLocaleDateString();
                pending_bill.time_str = d.toLocaleTimeString();
                pending_bill.max_session_consumption_to_date = 'TBC';
                pending_bill.total_consumption_to_date = 'TBC';
                pending_bill.total_consumption_kwh_session = 0;
                pending_bill.local_authority = getLocalAuthorityInfo(
                  customer
                ).name;
                pending_bill.forename = customer?.forename;
                pending_bill.surname = customer?.surname;
                pending_bill.total_consumption = 0;

                pending_bill.sessions = allLineItems.filter(
                  x => x.bill_id === pending_bill.id
                );

                pending_bill.to_charge = pending_bill.bill_cost;
                if (pending_bill.latest_stripe_balance) {
                  if (
                    pending_bill.bill_cost -
                      pending_bill.latest_stripe_balance <=
                    0
                  ) {
                    pending_bill.to_charge = 0;
                  } else {
                    pending_bill.to_charge =
                      pending_bill.bill_cost -
                      pending_bill.latest_stripe_balance;
                  }
                } else {
                  pending_bill.latest_stripe_balance = 0;
                }

                pending_bill.sessions.forEach(session => {
                  session.id = session.session_id;
                  session.location = 'tbc';
                  session.total_consumption = 0;
                  session.cost = session.amount;

                  pendingBillSessionLets = allSessionlets.filter(
                    sessionlet =>
                      sessionlet.bill_id === pending_bill.id &&
                      sessionlet.session_id === session.session_id
                  );
                  pendingBillSessionLets = _.orderBy(
                    pendingBillSessionLets,
                    'start_reading_timestamp',
                    'desc'
                  );

                  session.sessionlets = pendingBillSessionLets;
                  session.total_cost = 0;

                  pendingBillSessionLets.forEach(sessionlet => {
                    session.location = sessionlet.location;

                    sessionlet.total_consumption =
                      sessionlet.stop_reading_l1_energy -
                      sessionlet.start_reading_l1_energy;

                    sessionlet.total_consumption +=
                      sessionlet.stop_reading_l2_energy -
                      sessionlet.start_reading_l2_energy;

                    sessionlet.total_consumption +=
                      sessionlet.stop_reading_l3_energy -
                      sessionlet.start_reading_l3_energy;

                    pending_bill.total_consumption_kwh_session +=
                      sessionlet.total_consumption / 1000;

                    sessionlet.charging_location = 'TBC';

                    let theTariff = tariffs.find(
                      tariff => tariff.id === sessionlet.tariff_id
                    );

                    sessionlet.tariff = theTariff.cost;
                    sessionlet.cost =
                      (sessionlet.total_consumption / 1000 / 100) *
                      sessionlet.tariff;

                    session.total_cost += sessionlet.cost;
                    session.total_consumption += sessionlet.total_consumption;
                  });
                });

                pending_bill.sessions = _.orderBy(
                  pending_bill.sessions,
                  'line_item_no',
                  'asc'
                );
              });

              pending_bills = _.sortBy(pending_bills, [
                'local_authority',
                'surname',
                'bill_timestamp'
              ]);
              setPendingBills(pending_bills);

              let filtered = pending_bills.filter(x => x.archived === false);
              setFilteredPendingBills(filtered);
              setSummaryCost(filtered);
            });
        });
      });
  }, [allCustomers]); // eslint-disable-line

  return (
    <Segment
      style={{
        cursor: cursorState
      }}
      co
    >
      <Grid stretched>
        <Grid.Row>
          <Grid.Column
            style={{
              width: '200px'
            }}
          >
            <Header as="h2">Pending Bills</Header>
            Billing Total: {formatCurrency('£', pending_bills_summary)}
          </Grid.Column>
          <Grid.Column
            style={{
              width: '200px'
            }}
          >
            <Button
              style={{
                width: '100%',
                cursor: cursorState
              }}
              color="olive"
              onClick={() => {
                setCursorState('wait');
                servicesAPI
                  .BillingServices('refreshBalances')
                  .then(x => {
                    setCursorState('');
                    window.location.reload();
                  })
                  .catch(x => {
                    displayMessage(
                      'Failure',
                      'Failed to grab balance data from stripe. Please check the logs to ensure an Orion ticket has been raised.',
                      false
                    );
                  });
              }}
            >
              Refresh Stripe Balances
            </Button>
          </Grid.Column>
          <Grid.Column
            style={{
              width: '200px'
            }}
          >
            <Button
              style={{
                width: '100%',
                cursor: cursorState
              }}
              color="olive"
              onClick={() => {
                setCursorState('wait');
                filtered_pending_bills.forEach(bill => {
                  servicesAPI
                    .BillingServices('recalculateBill', bill.id)
                    .then(x => {
                      setCursorState('');
                      window.location.reload();
                    })
                    .then(() => {
                      displayMessage(
                        'Success',
                        'All bills and sessions successfully recalculated with their default Tariffs.',
                        true
                      );
                      setCursorState('');
                      window.location.reload();
                    })
                    .catch(x => {
                      displayMessage(
                        'Failure',
                        'Failed to recalculate Bills. Please check the logs for information.',
                        false
                      );
                    });
                });
              }}
            >
              Recalculate Bills
            </Button>
          </Grid.Column>
          <Grid.Column
            style={{
              fontSize: '20px',
              width: '200px'
            }}
          >
            <label>
              Show Active&nbsp;
              <input
                style={{ height: '25px', width: '25px' }}
                type="radio"
                value="ShowActive"
                name="ShowActive"
                checked={checkedArchiveRadio === 'ShowActive'}
                onChange={x => {
                  setArchiveRadioSelected('ShowActive');
                  onArchiveFilterChanged('ShowActive');
                }}
              />
            </label>
          </Grid.Column>
          <Grid.Column
            style={{
              fontSize: '20px',
              width: '200px'
            }}
          >
            <label>
              Show Archived&nbsp;
              <input
                style={{ height: '25px', width: '25px' }}
                type="radio"
                value="ShowArchived"
                name="ShowArchived"
                checked={checkedArchiveRadio === 'ShowArchived'}
                onChange={x => {
                  setArchiveRadioSelected('ShowArchived');
                  onArchiveFilterChanged('ShowArchived');
                }}
              />
            </label>
          </Grid.Column>
          <Grid.Column
            style={{
              fontSize: '20px',
              width: '200px'
            }}
          >
            <label>
              Show All&nbsp;
              <input
                style={{ height: '25px', width: '25px' }}
                type="radio"
                value="ShowAll"
                name="ShowAll"
                checked={checkedArchiveRadio === 'ShowAll'}
                onChange={x => {
                  setArchiveRadioSelected('ShowAll');
                  onArchiveFilterChanged('ShowAll');
                }}
              />
            </label>
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <></>
      <Divider hidden />
      <Grid>
        <Table celled fixed>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Date / Time</Table.HeaderCell>
              <Table.HeaderCell>
                Local
                <br />
                Authority
              </Table.HeaderCell>
              <Table.HeaderCell>
                Customer
                <br />
                Name
              </Table.HeaderCell>
              <Table.HeaderCell>
                Total Consumption
                <br />
                (kWh)
              </Table.HeaderCell>
              <Table.HeaderCell>
                Total
                <br />
                Cost (£)
              </Table.HeaderCell>
              <Table.HeaderCell>
                Stripe Balance
                <br />
                (Amount to Charge)
              </Table.HeaderCell>
              <Table.HeaderCell>Validation</Table.HeaderCell>
              <Table.HeaderCell>
                Stripe
                <br />
                Errors
              </Table.HeaderCell>
              <Table.HeaderCell>
                Credits
                <br />
                Applied
              </Table.HeaderCell>
              <Table.HeaderCell>Actions</Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {filtered_pending_bills.map(pending_bill => (
              <>
                <Table.Row
                  key={pending_bill.id}
                  onClick={clickPayload => {
                    if (
                      clickPayload.target.getAttribute('name') !== 'buttons'
                    ) {
                      if (pending_bill.id === activePendingBillId)
                        setActivePendingBill(null);
                      else setActivePendingBill(pending_bill.id);
                    }
                  }}
                  style={{
                    backgroundColor: greenRows.includes(pending_bill.id)
                      ? 'lightgreen'
                      : ''
                  }}
                >
                  <Table.Cell>
                    {pending_bill.date_str} {pending_bill.time_str}
                  </Table.Cell>
                  <Table.Cell>{pending_bill.local_authority}</Table.Cell>
                  <Table.Cell>
                    {pending_bill.forename} {pending_bill.surname}
                  </Table.Cell>
                  <Table.Cell style={{ textAlign: 'right' }}>
                    {pending_bill.total_consumption_kwh_session}
                  </Table.Cell>
                  <>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      {formatCurrency('£', pending_bill.bill_cost / 1000 / 100)}
                    </Table.Cell>
                    <Table.Cell style={{ textAlign: 'right' }}>
                      {formatCurrency(
                        '£',
                        pending_bill.latest_stripe_balance / 1000 / 100
                      )}
                      <br />(
                      {formatCurrency('£', pending_bill.to_charge / 1000 / 100)}
                      )
                    </Table.Cell>
                  </>
                  <>
                    <Table.Cell>
                      {pending_bill.validation_failed_reason}
                    </Table.Cell>
                    <Table.Cell>
                      {pending_bill.latest_stripe_error || ''}
                    </Table.Cell>
                    <Table.Cell>
                      {pending_bill.credits_applied || ''}
                    </Table.Cell>
                  </>
                  <>
                    <Table.Cell style={{ textAlign: 'right' }} name="buttons">
                      <Button
                        name="buttons"
                        style={{
                          width: '100%'
                        }}
                        color="olive"
                        disabled={
                          greyedOutProcessButtons.includes(pending_bill.id) ||
                          pending_bill.validation_failed_reason
                            ? true
                            : false
                        }
                        onClick={() => {
                          addRowToGreyedOutProcessButtons(pending_bill.id);
                          servicesAPI
                            .BillingServices('processBill', pending_bill.id)
                            .then(x => {
                              displayMessage(
                                'Success',
                                'Bill Successfully Charged',
                                true
                              );
                              pending_bill.paid = true;
                              addRowToGreenColour(pending_bill.id);
                            })
                            .catch(x => {
                              displayMessage(
                                'Failure',
                                'Bill Failed to be charged. Refresh the screen to see the error, or please check the logs to ensure an Orion ticket has been raised.',
                                false
                              );
                            });
                        }}
                      >
                        Process Bill
                      </Button>
                      &nbsp;
                      <Button
                        name="buttons"
                        style={{
                          width: '100%'
                        }}
                        color="olive"
                        disabled={
                          greyedOutModifyButtons.includes(pending_bill.id) ||
                          pending_bill.credits_applied
                            ? true
                            : false
                        }
                        onClick={() => {
                          addRowToGreyedOutModifyButtons(pending_bill.id);
                          servicesAPI
                            .BillingServices(
                              'applyLowTariff',
                              pending_bill.id,
                              1
                            )
                            .then(() => {
                              displayMessage(
                                'Success',
                                'Lower Tariff has been applied.',
                                true
                              );
                              onCreditApplied(
                                pending_bill.id,
                                '45p Tariff Applied'
                              );
                              setFilteredPendingBills(filtered_pending_bills);
                            })
                            .catch(() => {
                              displayMessage(
                                'Failure',
                                'Failed to apply Tariff. Please check the logs.',
                                false
                              );
                            });
                        }}
                      >
                        Apply Low Tariff
                      </Button>
                      &nbsp;
                      <Button
                        name="buttons"
                        style={{
                          width: '100%'
                        }}
                        color="olive"
                        disabled={
                          greyedOutModifyButtons.includes(pending_bill.id) ||
                          pending_bill.credits_applied
                            ? true
                            : false
                        }
                        onClick={() => {
                          addRowToGreyedOutModifyButtons(pending_bill.id);
                          servicesAPI
                            .BillingServices(
                              'creditEntireBill',
                              pending_bill.id,
                              1
                            )
                            .then(() => {
                              displayMessage(
                                'Success',
                                'Credit has been applied to Stripe. Note the bill still must be processed.',
                                true
                              );
                              onCreditApplied(
                                pending_bill.id,
                                'Entire Bill Credited to Stripe'
                              );
                              setFilteredPendingBills(filtered_pending_bills);
                            })
                            .catch(error => {
                              displayMessage(
                                'Failure',
                                'Failed to apply credit to stripe, or please check the logs.',
                                false
                              );
                              pending_bill.latest_stripe_error = error;
                              setFilteredPendingBills(filtered_pending_bills);
                            });
                        }}
                      >
                        Credit entire bill in Stripe
                      </Button>
                      &nbsp;
                      <Button
                        name="buttons"
                        style={{
                          width: '100%'
                        }}
                        color="olive"
                        disabled={
                          greyedOutProcessButtons.includes(pending_bill.id)
                            ? true
                            : false
                        }
                        onClick={() => {
                          addRowToGreyedOutProcessButtons(pending_bill.id);
                          servicesAPI
                            .BillingServices(
                              'archiveBill',
                              pending_bill.id,
                              !pending_bill.archived
                            )
                            .then(() => {
                              displayMessage(
                                'Success',
                                `Bill has been ${
                                  pending_bill.archived
                                    ? 'Marked Active'
                                    : 'Archived'
                                }`,
                                true
                              );
                              onArchiveButtonPressed(pending_bill.id);
                            })
                            .catch(error => {
                              displayMessage(
                                'Failure',
                                'Failed to Archive, check the logs.',
                                false
                              );
                            });
                        }}
                      >
                        {pending_bill.archived ? 'Mark Active' : 'Archive'}
                      </Button>
                    </Table.Cell>
                  </>
                </Table.Row>
                {pending_bill.id === activePendingBillId && (
                  <Table celled>
                    <Table.Header>
                      <Table.Row>
                        <Table.HeaderCell>Session ID</Table.HeaderCell>
                        <Table.HeaderCell>Start</Table.HeaderCell>
                        <Table.HeaderCell>End</Table.HeaderCell>
                        <Table.HeaderCell>Location</Table.HeaderCell>
                        <Table.HeaderCell>Consumption (Wh)</Table.HeaderCell>
                        <Table.HeaderCell>Cost</Table.HeaderCell>
                        <Table.HeaderCell>Line Item No</Table.HeaderCell>
                        <Table.HeaderCell>Stripe Line Item ID</Table.HeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {pending_bill.sessions &&
                        pending_bill.sessions.map(session => (
                          <>
                            <Table.Row>
                              <Table.Cell>{session.id}</Table.Cell>
                              <Table.Cell>
                                {dbUTCDatetimeToLocal(
                                  session.session_start_date
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                {dbUTCDatetimeToLocal(session.session_end_date)}
                              </Table.Cell>
                              <Table.Cell>{session.location}</Table.Cell>
                              <Table.Cell>
                                {session.total_consumption}
                              </Table.Cell>
                              <Table.Cell>
                                {formatCurrency('£', session.amount / 100000)}
                              </Table.Cell>
                              <Table.Cell>{session.line_item_no}</Table.Cell>
                              <Table.Cell>{session.line_item_id}</Table.Cell>
                            </Table.Row>
                            <Accordion>
                              <Accordion.Title
                                active={session.session_id === activeSessionId}
                                index={0}
                                onClick={() => {
                                  if (session.session_id === activeSessionId)
                                    setActiveSessionId(null);
                                  else setActiveSessionId(session.session_id);
                                }}
                              >
                                <Header as="h4">
                                  <Icon name="dropdown" />
                                  Sessionlets
                                </Header>
                              </Accordion.Title>
                              <Accordion.Content
                                active={session.session_id === activeSessionId}
                              >
                                <Table celled>
                                  <Table.Header>
                                    <Table.Row>
                                      <Table.HeaderCell>
                                        Sessionlet Start
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Sessionlet End
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Consumption (Wh)
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>Cost</Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Tariff
                                      </Table.HeaderCell>
                                      <Table.HeaderCell>
                                        Meter Tag ID
                                      </Table.HeaderCell>
                                    </Table.Row>
                                  </Table.Header>
                                  <Table.Body>
                                    {session.sessionlets &&
                                      session.sessionlets.map(sessionlet => (
                                        <Table.Row>
                                          <Table.Cell>
                                            {dbUTCDatetimeToLocal(
                                              sessionlet.start_reading_timestamp
                                            )}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {dbUTCDatetimeToLocal(
                                              sessionlet.stop_reading_timestamp
                                            )}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {sessionlet.total_consumption}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {formatCurrency(
                                              '£',
                                              sessionlet.cost
                                            )}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {sessionlet.tariff}
                                          </Table.Cell>
                                          <Table.Cell>
                                            {sessionlet.meter_tag_id}
                                          </Table.Cell>
                                        </Table.Row>
                                      ))}
                                  </Table.Body>
                                </Table>
                              </Accordion.Content>
                            </Accordion>
                          </>
                        ))}
                    </Table.Body>
                  </Table>
                )}
              </>
            ))}
          </Table.Body>
        </Table>
      </Grid>
    </Segment>
  );
};
